import { useEffect, useRef, useState } from 'react';
import Modal from 'react-native-modal';

import useAppNavigation from '../../hooks/useAppNavigation';
import { useIntervalFetchBookingsQuery } from '../../store/bookings';
import { PrimaryButton } from '../Button';
import Loader from '../Loader';
import { TextBody, TextH3 } from '../StyledText';
import { View } from '../Themed';

export function useEmbeddedWaitingModal() {
  const [embeddedWaitingModalVisible, setEmbeddedWaitingModalVisible] =
    useState(false);

  const showEmbeddedWaitingModal = () => {
    setEmbeddedWaitingModalVisible(true);
  };

  return {
    embeddedWaitingModalVisible,
    showEmbeddedWaitingModal,
  };
}

export default function EmbeddedWaitingModal({
  currentBookingsIds,
  locationId,
}: {
  locationId: number;
  currentBookingsIds: number[];
}) {
  const navigation = useAppNavigation();
  const initialCurrentBookingsIds = useRef([...currentBookingsIds]).current;

  // state flags
  const [timeoutHappened, setTimeoutHappened] = useState(false);
  const [success, setSuccess] = useState(false);
  const [newBookingId, setNewBookingId] = useState<number | null>(null);

  const { data: bookings } = useIntervalFetchBookingsQuery(!success);

  const fetchedBookingIds = bookings?.upcoming
    .filter((b) => b.location.id === locationId)
    .map((b) => b.id);

  useEffect(() => {
    if (!bookings) return;
    if (!fetchedBookingIds) return;
    if (fetchedBookingIds.length > initialCurrentBookingsIds.length) {
      const newBookingId = fetchedBookingIds.find(
        (id) => !initialCurrentBookingsIds.includes(id)
      );
      const booking = bookings.upcoming.find((b) => b.id === newBookingId);
      if (booking && booking.status === 'paid') {
        setSuccess(true);
        setNewBookingId(newBookingId ?? null);
      }
    }
  }, [bookings]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setTimeoutHappened(true);
    }, 120 * 1000);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <Modal isVisible>
      <View
        style={{
          backgroundColor: 'white',
          padding: 40,
          borderRadius: 10,
          maxWidth: 600,
          alignSelf: 'center',
        }}
      >
        <TextH3 style={{ marginBottom: 20, textAlign: 'center' }}>
          {success
            ? 'Payment Successful!'
            : timeoutHappened
              ? 'Payment Status Unknown'
              : 'Waiting for payment...'}
        </TextH3>
        <TextBody style={{ textAlign: 'center', marginBottom: 20 }}>
          {success
            ? 'Your payment has been processed successfully. You can view your booking details by clicking the button below.'
            : timeoutHappened
              ? "We haven't been able to confirm your payment yet. This could be due to system lag. You can check your bookings to see if the payment went through, or try booking again if needed."
              : 'We are waiting for payment to be completed. This may take a few minutes.'}
        </TextBody>
        <View style={{ marginTop: 20, alignItems: 'center' }}>
          {success ? (
            <PrimaryButton
              title="View Booking"
              onPress={() =>
                newBookingId
                  ? navigation.navigate('BookingDetails', {
                      id: newBookingId.toString(),
                    })
                  : undefined
              }
              style={{ minWidth: 200 }}
            />
          ) : timeoutHappened ? (
            <PrimaryButton
              title="View Bookings"
              onPress={() => navigation.navigate('Bookings', {})}
              style={{ minWidth: 200 }}
            />
          ) : (
            <Loader />
          )}
        </View>
      </View>
    </Modal>
  );
}
